// TTS Pricing Configuration
// Prices are per 1 million characters unless otherwise specified
// Last updated: 2025

export const TTS_PRICING = {
  // Google Cloud Text-to-Speech Standard Voices
  'google-standard': {
    pricePerMillion: 4.00,
    currency: 'USD',
    description: 'Google Standard voices (e.g., en-US-Standard-*)',
  },

  // Google Cloud Text-to-Speech Neural2 Voices
  'google-neural2': {
    pricePerMillion: 16.00, // Confirmed [1, 2]
    currency: 'USD',
    description: 'Google Neural2 voices (e.g., en-US-Neural2-*)',
  },

  // Google Cloud Text-to-Speech Studio Voices
  'google-studio': {
    pricePerMillion: 160.00, // Updated from $16.00 [1, 2]
    currency: 'USD',
    description: 'Google Studio voices (e.g., en-US-Studio-*)',
  },

  // Google Cloud Text-to-Speech Chirp3 HD Voices
  'google-chirp3-hd': {
    pricePerMillion: 30.00, // Updated from $64.00 [1, 2]
    currency: 'USD',
    description: 'Google Chirp3 HD voices',
  },

  // Google Gemini TTS (gemini-2.5-flash-tts)
  'gemini-flash-tts': {
    // NOTE: Actual pricing is token-based ($0.50/1M input text tokens + $10.00/1M output audio tokens).[2]
    // The per-character price is an estimation and not official.
    pricePerMillion: 16.00, // Estimated - similar to Neural2
    currency: 'USD',
    description: 'Gemini Flash TTS voices (Despina, etc.)',
  },

  // Google Gemini Pro TTS (gemini-2.5-pro-tts)
  'gemini-pro-tts': {
    // NOTE: Actual pricing is token-based ($1.00/1M input text tokens + $20.00/1M output audio tokens).[2]
    // The per-character price is an estimation and not official.
    pricePerMillion: 64.00, // Estimated - premium model
    currency: 'USD',
    description: 'Gemini Pro TTS voices',
  },

  // Google Gemini 3.1 Flash TTS Preview
  'gemini-3.1-flash-tts-preview': {
    tokenBased: true,
    inputPricePerMillionTokens: 1.00,
    outputPricePerMillionTokens: 20.00,
    textCharsPerToken: 4,
    audioTokensPerSecond: 25,
    charsPerMinute: 750,
    currency: 'USD',
    description: 'Gemini 3.1 Flash TTS Preview voices (Aoede SimpleTTS, Erinome)',
  },

  // Google Gemini 3.8 Flash TTS
  'gemini-3.8-flash-tts': {
    // NOTE: Not verified against Google's price list; assumes the same rates as 3.1 Flash TTS.
    tokenBased: true,
    inputPricePerMillionTokens: 1.00,
    outputPricePerMillionTokens: 20.00,
    textCharsPerToken: 4,
    audioTokensPerSecond: 25,
    charsPerMinute: 750,
    currency: 'USD',
    description: 'Gemini 3.8 Flash TTS voices (Aoede SimpleTTS)',
  },

  // Google Gemini 2.5 Pro Preview TTS
  'gemini-2.5-pro-preview-tts': {
    tokenBased: true,
    inputPricePerMillionTokens: 1.00,
    outputPricePerMillionTokens: 20.00,
    textCharsPerToken: 4,
    audioTokensPerSecond: 25,
    charsPerMinute: 750,
    currency: 'USD',
    description: 'Gemini 2.5 Pro Preview TTS voices',
  },

  // Azure Neural Voices
  'azure-neural': {
    pricePerMillion: 15.00, // Updated from $16.00 [3]
    currency: 'USD',
    description: 'Azure Neural voices (e.g., Raquel, Ava)',
  },

  // NOTE: Azure does not list a separate price for multilingual neural voices.
  // They are included under the standard 'azure-neural' pricing. [3]
  // The 'azure-multilingual' category has been removed.
};

/**
 * Maps voice IDs to their pricing categories
 */
export const VOICE_PRICING_MAP = {
  // Google Standard voices
  'en-US-Standard-D': 'google-standard',

  // Google Neural2 voices
  'en-US-Neural2-J': 'google-neural2',

  // Google Studio voices
  'en-US-Studio-O': 'google-studio',
  'de-DE-Studio-C': 'google-studio',
  'es-ES-Studio-C': 'google-studio',
  'fr-FR-Studio-A': 'google-studio',

  // Google Chirp3 HD voices
  'en-US-Chirp3-HD-Despina': 'google-chirp3-hd',
  'en-US-Chirp3-HD-Laomedeia': 'google-chirp3-hd',
  'pt-BR-Chirp3-HD-Despina': 'google-chirp3-hd',
  'de-DE-Chirp3-HD-Despina': 'google-chirp3-hd',
  'es-ES-Chirp3-HD-Despina': 'google-chirp3-hd',
  'fr-FR-Chirp3-HD-Despina': 'google-chirp3-hd',

  // Gemini TTS voices (Flash model)
  'en-US-Gemini-Despina': 'gemini-flash-tts',
  'pt-PT-Gemini-Despina': 'gemini-flash-tts',
  'pt-BR-Gemini-Despina': 'gemini-flash-tts',
  'de-DE-Gemini-Despina': 'gemini-flash-tts',
  'es-ES-Gemini-Despina': 'gemini-flash-tts',
  'fr-FR-Gemini-Despina': 'gemini-flash-tts',
  'bg-BG-Gemini-Despina': 'gemini-flash-tts',

  // Gemini 3.1 Flash TTS Preview voices
  'en-US-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'en-US-Gemini-Erinome-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'pt-PT-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'pt-PT-Gemini-Erinome-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'pt-BR-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'pt-BR-Gemini-Erinome-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'es-ES-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'es-ES-Gemini-Erinome-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'fr-FR-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'fr-FR-Gemini-Erinome-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'de-DE-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'bg-BG-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'ar-XA-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',
  'it-IT-Gemini-Aoede-SimpleTTS': 'gemini-3.1-flash-tts-preview',

  // Gemini 3.8 Flash TTS voices
  'en-US-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',
  'pt-PT-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',
  'pt-BR-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',
  'es-ES-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',
  'fr-FR-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',
  'de-DE-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',
  'bg-BG-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',
  'ar-XA-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',
  'it-IT-Gemini-Aoede-SimpleTTS-38': 'gemini-3.8-flash-tts',

  // Gemini 2.5 Pro Preview TTS voices
  'en-US-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'en-US-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'en-US-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'en-US-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',
  'pt-PT-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'pt-PT-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'pt-PT-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'pt-PT-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',
  'pt-BR-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'pt-BR-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'pt-BR-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'pt-BR-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',
  'es-ES-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'es-ES-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'es-ES-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'es-ES-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',
  'fr-FR-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'fr-FR-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'fr-FR-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'fr-FR-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',
  'de-DE-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'de-DE-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'de-DE-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'de-DE-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',
  'bg-BG-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'bg-BG-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'bg-BG-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'bg-BG-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',
  'ar-XA-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'ar-XA-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'ar-XA-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'ar-XA-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',
  'it-IT-Gemini-Aoede-SimpleTTS-Legacy': 'gemini-2.5-pro-preview-tts',
  'it-IT-Gemini-Aoede-Preview': 'gemini-2.5-pro-preview-tts',
  'it-IT-Gemini-Aoede-SimpleTTS-Pro': 'gemini-pro-tts',
  'it-IT-Gemini-Despina-SimpleTTS': 'gemini-2.5-pro-preview-tts',

  // Azure Neural voices
  'pt-PT-RaquelNeural': 'azure-neural',
  'bg-BG-KalinaNeural': 'azure-neural',
  'en-US-AvaMultilingualNeural-pt-PT': 'azure-neural', // Re-mapped from azure-multilingual
  'en-US-AvaMultilingualNeural-pt-BR': 'azure-neural', // Re-mapped from azure-multilingual
  'fr-FR-AvaMultilingual': 'azure-neural', // Re-mapped from azure-multilingual
  'de-DE-AvaMultilingual': 'azure-neural', // Re-mapped from azure-multilingual
  'es-ES-AvaMultilingual': 'azure-neural', // Re-mapped from azure-multilingual
};

/**
 * Calculate the cost of generating audio
 * @param {string} voiceId - The voice ID (e.g., 'en-US-Gemini-Despina')
 * @param {number} characterCount - Number of characters in the text
 * @returns {object} Cost information
 */
export const calculateTTSCost = (voiceId, characterCount) => {
  // Get pricing category for this voice
  const pricingCategory = VOICE_PRICING_MAP[voiceId];

  if (!pricingCategory) {
    return {
      cost: 0,
      currency: 'USD',
      pricePerMillion: 0,
      characterCount,
      category: 'unknown',
      error: 'Pricing not available for this voice',
    };
  }

  const pricing = TTS_PRICING[pricingCategory];
  let cost = 0;

  if (pricing.tokenBased) {
    const estimatedTextTokens = characterCount / (pricing.textCharsPerToken || 4);
    const estimatedAudioSeconds = (characterCount / (pricing.charsPerMinute || 750)) * 60;
    const estimatedAudioTokens = estimatedAudioSeconds * (pricing.audioTokensPerSecond || 25);
    cost =
      (estimatedTextTokens / 1000000) * pricing.inputPricePerMillionTokens +
      (estimatedAudioTokens / 1000000) * pricing.outputPricePerMillionTokens;
  } else {
    // Calculate cost: (characters / 1,000,000) * price per million
    cost = (characterCount / 1000000) * pricing.pricePerMillion;
  }

  const pricePerMillion = characterCount > 0
    ? (cost / characterCount) * 1000000
    : (pricing.pricePerMillion || 0);

  return {
    cost: parseFloat(cost.toFixed(6)), // Round to 6 decimal places
    costFormatted: cost < 0.01? `$${cost.toFixed(6)}` : `$${cost.toFixed(4)}`,
    currency: pricing.currency,
    pricePerMillion,
    characterCount,
    category: pricingCategory,
    description: pricing.description,
  };
};

/**
 * Get pricing information for a voice without calculating cost
 * @param {string} voiceId - The voice ID
 * @returns {object} Pricing information
 */
export const getVoicePricing = (voiceId) => {
  const pricingCategory = VOICE_PRICING_MAP[voiceId];

  if (!pricingCategory) {
    return {
      available: false,
      error: 'Pricing not available for this voice',
    };
  }

  return {
    available: true,
    ...TTS_PRICING[pricingCategory],
    category: pricingCategory,
  };
};
